import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ROLE, SALE_ORDER_STATUS } from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { api } from "../../../../shared/hooks/api";
import { color } from "../../../../shared/styles";
import UserIcon from "../../../../shared/svg/UserIcon";
import apiMethods from "../../../../utils/api";
import { error, success, warning } from "../../../../utils/toast";
import { InfoBoxTitle, InfoBoxWrapper } from "../Styled";
import { AssigneeBox } from "./Styled";

const ROLE_DISPLAY: Record<string, string> = {
    sale_staff: "Nhân viên sale",
    production_staff: "Hậu cần điều phối",
    logistic_manager: "Hậu cần điều phối",
    storage_keeper: "Thủ kho nhập kho",
    first_storage_keeper: "Thủ kho 1",
    second_storage_keeper: "Thủ kho 2",
    factory_manager_one: "Xưởng Phuy Thùng",
    factory_manager_two: "Xưởng Cơ Khí Ô Tô",
    deputy_factory_manager_one: "Xưởng Phuy Thùng",
    deputy_factory_manager_two: "Xưởng Cơ Khí Ô Tô",
};

const getAssignmentErrorMessage = (err: any, role?: string) => {
    const roleText = ROLE_DISPLAY[role || ""] || "Nhân sự";
    try {
        const raw =
            typeof err === "string"
                ? err
                : err?.detail || err?.message || JSON.stringify(err);
        if (/UniqueViolation|duplicate key|already exists/i.test(raw)) {
            return `Vui lòng bỏ chọn trước khi bàn giao người khác`;
        } else if (/Cannot create an assignment for/.test(raw)) {
            return `Không thể bàn giao khi trạng thái đơn hàng là hoàn thành hoặc đã hủy`;
        }
        return `${roleText} - bàn giao thất bại`;
    } catch (_e) {
        return `${roleText} - bàn giao thất bại`;
    }
};

const Content = ({
                     onChangeValue,
                     assignees,
                     title,
                     role,
                     locked = false,
                     isUpdating = false,
                     canEditOverride,
                 }: {
    onChangeValue: Function;
    assignees: any[];
    title: string;
    role?: string;
    locked?: boolean;
    isUpdating?: boolean;
    canEditOverride?: boolean;
}) => {
    const getUsersQuery = () => {
        const upperRole = role?.toUpperCase();
        if (upperRole === ROLE.PRODUCTION_STAFF) {
            return `/users/?role=${ROLE.LOGISTIC_MANAGER},${ROLE.PRODUCTION_STAFF}`;
        } else if (upperRole === ROLE.STORAGE_KEEPER) {
            return `/users/?role=${ROLE.FIRST_STORAGE_KEEPER},${ROLE.SECOND_STORAGE_KEEPER}`;
        } else if (upperRole === ROLE.FACTORY_MANAGER_ONE) {
            return `/users/?role=${ROLE.FACTORY_MANAGER_ONE},${ROLE.DEPUTY_FACTORY_MANAGER_ONE}`;
        } else if (upperRole === ROLE.FACTORY_MANAGER_TWO) {
            return `/users/?role=${ROLE.FACTORY_MANAGER_TWO},${ROLE.DEPUTY_FACTORY_MANAGER_TWO}`;
        } else {
            return `/users/?role=${upperRole}`;
        }
    };

    const [{ data, isLoading }] = api.get(getUsersQuery());

    const triggerRef = useRef<HTMLDivElement>(null);
    const [menuWidth, setMenuWidth] = useState<number>(260);
    useEffect(() => {
        const updateWidth = () => {
            const w = triggerRef.current?.offsetWidth || 260;
            setMenuWidth(Math.max(w, 260));
        };
        updateWidth();
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, [assignees]);

    const {
        user: { role: currentRole },
    } = useContext(AuthenticationContext);

    const canEdit = (toEditRole?: string) => {
        switch (toEditRole) {
            case "sale_staff":
                return [ROLE.MANAGER].includes(currentRole);
            case "logistic_staff":
                return [ROLE.MANAGER, ROLE.SALE_STAFF, ROLE.SALE_MANAGER].includes(currentRole);
            case "production_staff":
                return [ROLE.MANAGER, ROLE.SALE_STAFF, ROLE.SALE_MANAGER].includes(currentRole);
            case "purchasing_staff":
                return [ROLE.MANAGER, ROLE.PRODUCTION_STAFF].includes(
                    currentRole,
                );
            case "first_storage_keeper":
            case "second_storage_keeper":
                return [ROLE.MANAGER, ROLE.PRODUCTION_STAFF, ROLE.PURCHASING_STAFF].includes(
                    currentRole,
                );
            case "storage_keeper":
                return [ROLE.MANAGER, ROLE.PRODUCTION_STAFF, ROLE.PURCHASING_STAFF].includes(
                    currentRole,
                );
            case "factory_manager_one":
            case "factory_manager_two":
            case "deputy_factory_manager_one":
            case "deputy_factory_manager_two":
                return [ROLE.MANAGER, ROLE.PRODUCTION_STAFF, ROLE.PURCHASING_STAFF].includes(currentRole);
            case "deliverer":
                return [
                    ROLE.MANAGER,
                    ROLE.STORAGE_KEEPER,
                    ROLE.FIRST_STORAGE_KEEPER,
                    ROLE.SECOND_STORAGE_KEEPER,
                    ROLE.LOGISTIC_STAFF,
                ].includes(currentRole);
            case "manager":
                return [ROLE.MANAGER].includes(currentRole);
            default:
                return false;
        }
    };
    const isEditable = canEditOverride ?? canEdit(role);

    // Render read-only (locked) view without dropdown when locked
    if (locked) {
        return (
            <AssigneeBox>
                <div className="left">
                    <div className="title">{title}</div>
                    <div className="content locked" style={{ cursor: "default" }}>
                        {isUpdating
                            ? "Đang cập nhật..."
                            : (assignees && assignees.length > 0)
                            ? (
                                <>
                                    {assignees.map((item: any, idx: number) => (
                                        <span key={item?.user_id || idx} className="assignee-item">
                                            <span className="assignee-icon"><UserIcon /></span>
                                            <span className="assignee-text">
                                                <span className="assignee-name">{item?.name || "Không rõ tên"}</span>
                                                {item?.email ? (
                                                    <span className="assignee-email">{item.email}</span>
                                                ) : null}
                                            </span>
                                            {item?.is_primary ? (
                                                <span title="Phụ trách chính" className="primary-badge">Chính</span>
                                            ) : null}
                                            {idx < assignees.length - 1 ? (
                                                <span className="assignee-sep">•</span>
                                            ) : null}
                                        </span>
                                    ))}
                                </>
                            )
                            : ("Chưa bàn giao")}
                    </div>
                </div>
            </AssigneeBox>
        );
    }

    return (
        <AssigneeBox>
            <DropdownMenu.Root>
                <div className="left">
                    <div className="title">{title}</div>
                    <DropdownMenu.Trigger asChild>
                        <div ref={triggerRef} className="content"
                             style={{ cursor: isEditable ? "pointer" : "default" }}>
                            {(assignees && assignees.length > 0)
                                ? (
                                    <>
                                        {assignees.map((item: any, idx: number) => (
                                            <span key={item?.user_id || idx} className="assignee-item">
                                                <span className="assignee-icon"><UserIcon /></span>
                                                <span className="assignee-text">
                                                    <span
                                                        className="assignee-name">{item?.name || "Không rõ tên"}</span>
                                                    {item?.email ? (
                                                        <span className="assignee-email">{item.email}</span>
                                                    ) : null}
                                                </span>
                                                {item?.is_primary ? (
                                                    <span title="Phụ trách chính" className="primary-badge">Chính</span>
                                                ) : null}
                                            </span>
                                        ))}
                                    </>
                                )
                                : ("Chưa bàn giao")}
                        </div>
                    </DropdownMenu.Trigger>
                </div>

                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        side="bottom"
                        align="start"
                        sideOffset={6}
                        style={{
                            background: color.emphasis.NEUTRAL_N0,
                            border: `1px solid ${color.secondary.BORDER}`,
                            borderRadius: 10,
                            padding: 8,
                            zIndex: 10000,
                            boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
                            minWidth: menuWidth,
                            width: menuWidth,
                            maxHeight: 320,
                            overflowY: "auto",
                            fontFamily: "Noto Sans",
                        }}
                    >
                        <DropdownMenu.Item disabled style={{
                            padding: "8px 10px",
                            fontSize: 12,
                            color: color.secondary.DARK_GREY,
                            cursor: "default",
                            fontFamily: "Noto Sans",
                        }}>
                            {isLoading ? "Đang tải..." : `Chọn ${ROLE_DISPLAY[role || ""] || "người phụ trách"}`}
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator
                            style={{ height: 1, background: color.secondary.BORDER, margin: "6px 0" }} />
                        <DropdownMenu.Item
                            onSelect={() => onChangeValue(undefined)}
                            style={{
                                padding: "8px 10px",
                                fontSize: 14,
                                cursor: "pointer",
                                borderRadius: 6,
                                fontFamily: "Noto Sans",
                                color: color.secondary.CHARCOAL,
                            }}
                        >
                            Bỏ chọn
                        </DropdownMenu.Item>
                        {(data?.results || []).map((user: any) => (
                            <DropdownMenu.Item
                                key={user.id}
                                onSelect={() => onChangeValue(user)}
                                style={{
                                    padding: "8px 10px",
                                    fontSize: 14,
                                    cursor: "pointer",
                                    borderRadius: 6,
                                    fontFamily: "Noto Sans",
                                    color: color.secondary.CHARCOAL,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <span>{user.display_name || user.username}</span>
                                {(assignees || []).some((a: any) => a?.user_id === user.id) ? (
                                    <span style={{ color: color.primary.BLUE_B100 }}>✓</span>
                                ) : null}
                            </DropdownMenu.Item>
                        ))}
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        </AssigneeBox>
    );
};

export interface AssigneeField {
    title: string;
    fieldName: string;
    role?: string;
    assignmentRoles?: string[];
    canEdit?: boolean;
}

export const getAssignmentRolesForField = (field: AssigneeField) => {
    if (field.assignmentRoles?.length) {
        return field.assignmentRoles.map((item) => item.toLowerCase());
    }
    const role = (field.role || "").toLowerCase();
    if (role === "factory_manager_one") {
        return ["factory_manager_one", "deputy_factory_manager_one"];
    }
    if (role === "factory_manager_two") {
        return ["factory_manager_two", "deputy_factory_manager_two"];
    }
    return role ? [role] : [];
};

export const getAssigneesForAssignmentField = (
    assignmentByRole: Record<string, any[]>,
    field: AssigneeField,
) =>
    getAssignmentRolesForField(field).flatMap(
        (assignmentRole) => assignmentByRole[assignmentRole] || [],
    );

export const Assignees = ({
                              data: saleOrder,
                              updateData,
                              fetchData,
                              fields,
                              onAssignmentsUpdated,
                              disabledEdit = false,
                              ignoreStatusLock = false,
                          }: {
    data: any;
    updateData?: Function;
    fetchData?: any;
    fields: AssigneeField[];
    onAssignmentsUpdated?: () => void;
    disabledEdit?: boolean;
    ignoreStatusLock?: boolean;
}) => {
    const [updatingField, setUpdatingField] = useState<string | null>(null);
    // Fetch current assignments for this sale order (new API/state source)
    const [{ data: assignmentData }, fetchAssignments] = api.get(
        `/product-flow/v2/assignments/`,
        { sale_order_id: saleOrder?.id },
        !saleOrder?.id,
    );

    const assignmentByRole = useMemo(() => {
        const map: Record<string, any[]> = {};
        // New API shape: { ROLE_NAME: [{ id, user_id|user:{...}, role, ... }, ...], ... }
        if (assignmentData && typeof assignmentData === "object" && !Array.isArray(assignmentData)) {
            Object.keys(assignmentData as any).forEach((key) => {
                const roleKey = String(key).toLowerCase();
                const value = (assignmentData as any)[key];
                const arr = Array.isArray(value) ? value : [];
                map[roleKey] = arr.map((it: any) => {
                    const u = it.user || {};
                    return {
                        assignment_id: it.assignment_id || it.id,
                        user_id: it.user_id || u.id || u.user_id,
                        role: it.role || key,
                        is_primary: it.is_primary ?? u.is_primary ?? false,
                        name: it.name || u.display_name || u.username || u.name,
                        email: it.email || u.email,
                    };
                });
            });
            return map;
        }
        // Fallback for older shape (array with { role, user })
        const list = Array.isArray(assignmentData)
            ? assignmentData
            : assignmentData?.results || [];
        list.forEach((it: any) => {
            if (it?.role) {
                const roleKey = String(it.role).toLowerCase();
                if (!map[roleKey]) map[roleKey] = [];
                const u = it.user || {};
                map[roleKey].push({
                    assignment_id: it.assignment_id || it.id,
                    user_id: u.id || u.user_id,
                    role: it.role,
                    is_primary: it.is_primary ?? u.is_primary ?? false,
                    name: u.display_name || u.username || u.name,
                    email: u.email,
                });
            }
        });
        return map;
    }, [assignmentData]);

    const { user: { role } } = useContext(AuthenticationContext);

    const getAssigneesForField = (field: AssigneeField) =>
        getAssigneesForAssignmentField(assignmentByRole, field);

    const onChangeAssignee = (field: AssigneeField) => async (assignee: any) => {
        if (updatingField) return;
        setUpdatingField(field.fieldName);
        try {
            const role = field.role;
            const current = getAssigneesForField(field);

            // If "Bỏ chọn" clicked → unassign currently assigned user(s) for this role
            if (!assignee) {
                if (!current.length) {
                    success("THÀNH CÔNG", `${ROLE_DISPLAY[role || ""] || "Nhân sự"} - không có thay đổi`);
                    return;
                }
                const messages: string[] = [];
                for (const a of current) {
                    const assignmentId = a?.assignment_id || a?.id;
                    if (!assignmentId) continue;
                    const payload = {
                        name: a?.name || "",
                        role: (a?.role || role || "").toUpperCase(),
                    };
                    const res: any = await apiMethods.post(
                        `/product-flow/v2/assignments/${assignmentId}/unassigned/`,
                        payload,
                    );
                    if (res) {
                        const resultRole = (res?.role || payload.role || "").toLowerCase();
                        const roleText = ROLE_DISPLAY[resultRole] || (res?.role || payload.role || "");
                        const nameText = res?.name || payload.name || "";
                        const detailText = res?.detail || "Bỏ bàn giao";
                        messages.push(`${detailText}${nameText ? ` - ${nameText}` : ""}${roleText ? ` (${roleText})` : ""}`);
                    }
                }
                await fetchAssignments();
                if (onAssignmentsUpdated) onAssignmentsUpdated();
                success("THÀNH CÔNG", messages.join("; ") || `${ROLE_DISPLAY[role || ""] || "Nhân sự"} - bỏ chọn thành công`);
                return;
            }

            // If the selected user is already assigned → cancel assignment
            if (assignee?.id && current.some((a: any) => a?.user_id === assignee.id)) {
                const existingAssignment = current.find((a: any) => a?.user_id === assignee.id);
                const assignmentId = existingAssignment?.assignment_id || existingAssignment?.id;
                const res: any = await apiMethods.post(
                    `/product-flow/v2/assignments/${assignmentId}/unassigned/`);
                if (res) {
                    success("THÀNH CÔNG", `Hủy bàn giao cho ${assignee.display_name || assignee.username || ""} thành công`);
                    await fetchAssignments();
                    if (onAssignmentsUpdated) onAssignmentsUpdated();
                    return;
                }
            }

            // Assign the selected user
            const allowMultipleAssignment = [ROLE.PRODUCTION_STAFF, ROLE.FACTORY_MANAGER_ONE, ROLE.FACTORY_MANAGER_TWO, ROLE.DEPUTY_FACTORY_MANAGER_ONE, ROLE.DEPUTY_FACTORY_MANAGER_TWO];
            if (!allowMultipleAssignment.includes(assignee?.role) && current.length > 0) {
                warning("CẢNH BÁO", `Không thể bàn giao cho nhiều ${ROLE_DISPLAY[role || "người"]} cùng lúc`);
                return;
            }

            const res: any = await apiMethods.post(
                "/product-flow/v2/assignments/",
                {
                    sale_order_id: saleOrder?.id,
                    user_id: assignee?.id || "",
                    role: (assignee?.role || "").toUpperCase(),
                },
            );
            await fetchAssignments();
            if (onAssignmentsUpdated) onAssignmentsUpdated();
            {
                const resultRole = (res?.role || role || "").toLowerCase();
                const roleText = ROLE_DISPLAY[resultRole] || (res?.role || role || "");
                const nameText = res?.name || assignee?.display_name || assignee?.username || assignee?.name || "";
                const detailText = res?.detail || "Bàn giao";
                success("THÀNH CÔNG", `${detailText}${nameText ? ` - ${nameText}` : ""}${roleText ? ` (${roleText})` : ""}`);
            }
        } catch (err) {
            error(getAssignmentErrorMessage(err, role));
        } finally {
            setUpdatingField(null);
        }
    };

    return (
        <InfoBoxWrapper>
            <InfoBoxTitle>Phụ trách</InfoBoxTitle>
            {fields.map((item) => {
                const currentStatus = String(saleOrder?.status).toUpperCase();
                const r = (item.role || "").toLowerCase();
                const isLocked = (() => {
                    if (
                        role === ROLE.FIRST_STORAGE_KEEPER
                        || role === ROLE.SECOND_STORAGE_KEEPER
                        || role === ROLE.STORAGE_KEEPER
                    ) {
                        return true;
                    }
                    switch (currentStatus) {
                        case SALE_ORDER_STATUS.LOGISTIC_REVIEWED:
                            return r === "sale_staff";
                        case SALE_ORDER_STATUS.MATERIAL_REQUESTED_AND_MANUFACTURING:
                            return r === "sale_staff" || r === "production_staff";
                        case SALE_ORDER_STATUS.IMPORTING_AND_EXPORTING:
                            return r === "sale_staff" || r === "production_staff" || r === "factory_manager_one" || r === "factory_manager_two" || r === "deputy_factory_manager_one" || r === "deputy_factory_manager_two";
                        default:
                            return false;
                    }
                })();

                return (
                    <Content
                        key={item.fieldName}
                        title={item.title}
                        onChangeValue={onChangeAssignee(item)}
                        assignees={getAssigneesForField(item)}
                        role={item.role}
                        locked={
                            disabledEdit ||
                            (!ignoreStatusLock && isLocked) ||
                            updatingField !== null
                        }
                        isUpdating={updatingField === item.fieldName}
                        canEditOverride={item.canEdit}
                    />
                );
            })}
        </InfoBoxWrapper>
    );
};
